/**
 * Applying a patch to a row (PLAN §6.1): creation, the per-field LWW merge
 * (`lww.ts`), soft delete, text fields, and the ownership rule for locked
 * fields (D2). Also the whole-row merge used by catch-up.
 *
 *     handler: parse (api-contract) → validatePatch (validation.ts) → applyPatch → store
 *
 * `applyPatch` enforces what makes a row *coherent* whatever the caller
 * checked: identity, creation, ownership, text. Schema, parent rules and
 * permission are `validatePatch`'s. Both refuse with the same vocabulary
 * (`rejections.ts`).
 *
 * **All or nothing.** A patch with any refused field is refused whole: the
 * client rolls back its optimistic change (PLAN §6.4) and a half-applied patch
 * would leave it guessing which half. A field that merely LOSES the LWW
 * comparison is not refused — the patch is simply stale there, which is
 * normal under concurrency, and it is absent from `applied`.
 *
 * **Soft delete** is `set: {deleted: true}` — an ordinary register, so an
 * undelete is `deleted: false` under a newer HLC, and a concurrent edit of a
 * deleted row still lands (and shows once the row is restored).
 *
 * Pure: returns new objects and never mutates its inputs.
 */
import { tryParseHlc, compareHlc } from './hlc.js';
import { isTextField, type RowKind, ROOT_KINDS } from './fields.js';
import { applyLwwPatch, isValidFieldPath, mergeLwwRows } from './lww.js';
import type { JsonValue } from './primitives.js';
import { rejection, type PatchRejection } from './rejections.js';
import type { Row, RowPatch, SourceSystem } from './row.js';
import { applyTextUpdate, mergeTextStates } from './text.js';

export type ApplyContext = {
  /**
   * The source system the AUTHENTICATED writer belongs to — decided by the
   * ingress from the credential (a user session is `native`, the MOS gateway's
   * credential is `mos`), never read from the patch.
   */
  readonly writer: SourceSystem;
  /** The `changedAt` ULID the storage minted for this write. */
  readonly changedAt: string;
};

export type ApplyResult = {
  /** The new row; the input row (or null) unchanged when nothing applied or the patch was refused. */
  readonly row: Row | null;
  /** Sorted field paths that changed, text fields included. Empty when stale or refused. */
  readonly applied: readonly string[];
  /** Why the patch was refused. Non-empty means nothing was applied. */
  readonly rejected: readonly PatchRejection[];
};

type Base = { readonly ok: true; readonly row: Row; readonly creating: boolean } | { readonly ok: false; readonly rejected: PatchRejection[] };

function formatRejections(patch: RowPatch): PatchRejection[] {
  const out: PatchRejection[] = [];
  if (tryParseHlc(patch.hlc) === null) out.push(rejection('invalid-hlc', null, `malformed HLC ${JSON.stringify(patch.hlc)}`));
  for (const path of [...Object.keys(patch.set), ...Object.keys(patch.text ?? {})]) {
    if (!isValidFieldPath(path)) out.push(rejection('invalid-field-path', path, 'malformed field path'));
  }
  return out;
}

/** Whether a row of `kind` heads its own partition (`rundownId === rowId`). */
function headsPartition(kind: RowKind): boolean {
  return ROOT_KINDS.includes(kind);
}

function newRowFor(patch: RowPatch, ctx: ApplyContext): Base {
  const create = patch.create;
  if (create === undefined) {
    return { ok: false, rejected: [rejection('unknown-row', null, `no row ${patch.rowId} and the patch does not create it`)] };
  }
  const out: PatchRejection[] = [];
  for (const required of ['parentId', 'ord']) {
    if (!Object.hasOwn(patch.set, required)) {
      out.push(rejection('incomplete-create', required, `a creation patch must set ${required}`));
    }
  }
  if (create.source.system !== ctx.writer) {
    out.push(rejection('source-mismatch', null, `a ${ctx.writer} writer cannot create a ${create.source.system} row`));
  }
  if (headsPartition(create.kind) !== (create.rundownId === patch.rowId)) {
    out.push(
      rejection(
        'invalid-create',
        null,
        headsPartition(create.kind)
          ? `a ${create.kind} row heads its own partition: rundownId must equal rowId`
          : `a ${create.kind} row lives in a rundown's partition: rundownId must not equal rowId`,
      ),
    );
  }
  if (out.length > 0) return { ok: false, rejected: out };
  const row: Row = {
    rowId: patch.rowId,
    rundownId: create.rundownId,
    kind: create.kind,
    parentId: null,
    ord: 'a0',
    deleted: false,
    f: {},
    h: {},
    source: create.source,
    lockedFields: [...create.lockedFields],
    text: {},
    version: 0,
    changedAt: ctx.changedAt,
  };
  return { ok: true, row, creating: true };
}

function baseRowFor(row: Row | null, patch: RowPatch, ctx: ApplyContext): Base {
  const malformed = formatRejections(patch);
  if (malformed.length > 0) return { ok: false, rejected: malformed };
  if (row === null) return newRowFor(patch, ctx);
  if (row.rowId !== patch.rowId) {
    return { ok: false, rejected: [rejection('row-mismatch', null, `patch for ${patch.rowId} applied to ${row.rowId}`)] };
  }
  const create = patch.create;
  if (create !== undefined && (create.kind !== row.kind || create.rundownId !== row.rundownId)) {
    return {
      ok: false,
      rejected: [rejection('create-mismatch', null, `row ${row.rowId} exists as a ${row.kind} in ${row.rundownId}`)],
    };
  }
  return { ok: true, row, creating: false };
}

/**
 * Whether writing `path` touches a locked entry: the entry itself, a leaf below a
 * locked namespace, or a parent of a locked leaf (writing `params.vs` whole would
 * set a locked `params.vs.clip` once expanded on read).
 */
export function isLocked(lockedFields: readonly string[], path: string): boolean {
  return lockedFields.some((locked) => path === locked || path.startsWith(`${locked}.`) || locked.startsWith(`${path}.`));
}

const STRUCTURAL_TYPES: Readonly<Record<string, (value: unknown) => boolean>> = {
  parentId: (v) => v === null || typeof v === 'string',
  ord: (v) => typeof v === 'string',
  deleted: (v) => typeof v === 'boolean',
};

/** An own property's value; never an inherited one (`constructor`, `__proto__`). */
function own<T>(record: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.hasOwn(record, key) ? record[key] : undefined;
}

function setRejections(row: Row, set: Readonly<Record<string, JsonValue>>, owner: boolean): PatchRejection[] {
  const out: PatchRejection[] = [];
  for (const [path, value] of Object.entries(set)) {
    if (isTextField(row.kind, path)) {
      out.push(rejection('text-field-set', path, 'a text field is written as a Yjs update in `text`, not in `set`'));
    }
    const typeCheck = own(STRUCTURAL_TYPES, path);
    if (typeCheck !== undefined && !typeCheck(value)) {
      out.push(rejection('invalid-value', path, `wrong type for ${path}`));
    }
    if (!owner && isLocked(row.lockedFields, path)) {
      out.push(rejection('locked-field', path, `${path} is owned by ${row.source.system}`));
    }
  }
  return out;
}

function textRejections(row: Row, text: Readonly<Record<string, string>>, owner: boolean): PatchRejection[] {
  const out: PatchRejection[] = [];
  for (const path of Object.keys(text)) {
    if (!isTextField(row.kind, path)) {
      out.push(rejection('unknown-text-field', path, `${path} is not a text field of a ${row.kind}`));
    } else if (!owner && isLocked(row.lockedFields, path)) {
      out.push(rejection('locked-field', path, `${path} is owned by ${row.source.system}`));
    }
  }
  return out;
}

function contentRejections(row: Row, patch: RowPatch, ctx: ApplyContext): PatchRejection[] {
  const owner = row.source.system === ctx.writer;
  return [...setRejections(row, patch.set, owner), ...textRejections(row, patch.text ?? {}, owner)];
}

function laterHlc(current: string | undefined, candidate: string): string {
  return current === undefined || compareHlc(candidate, current) > 0 ? candidate : current;
}

type TextOutcome =
  | {
      readonly ok: true;
      readonly f: Record<string, JsonValue>;
      readonly h: Record<string, string>;
      readonly text: Record<string, string>;
      readonly applied: string[];
    }
  | { readonly ok: false; readonly rejected: PatchRejection[] };

function applyTextFields(
  row: Row,
  lww: { readonly f: Readonly<Record<string, JsonValue>>; readonly h: Readonly<Record<string, string>> },
  patch: RowPatch,
): TextOutcome {
  const f: Record<string, JsonValue> = { ...lww.f };
  const h: Record<string, string> = { ...lww.h };
  const text: Record<string, string> = { ...row.text };
  const applied: string[] = [];
  const rejected: PatchRejection[] = [];
  for (const [path, update] of Object.entries(patch.text ?? {}).sort(([a], [b]) => (a < b ? -1 : 1))) {
    const result = applyTextUpdate(own(text, path) ?? null, update);
    if (!result.ok) {
      rejected.push(rejection('invalid-text-update', path, result.error));
      continue;
    }
    if (!result.changed) continue;
    text[path] = result.state;
    f[path] = result.text;
    // Informational for a text field: the merge is Yjs, not LWW. It records
    // the latest write that touched the field, and keeps f/h key-aligned.
    h[path] = laterHlc(own(h, path), patch.hlc);
    applied.push(path);
  }
  if (rejected.length > 0) return { ok: false, rejected };
  return { ok: true, f, h, text, applied };
}

/**
 * The top-level copies of the structural registers. The fallbacks are
 * reachable only for a field never written, which `applyPatch` makes
 * impossible (creation requires parentId and ord and sets deleted) — they keep
 * the function total rather than asserting.
 */
function structuralOf(f: Readonly<Record<string, JsonValue>>): Pick<Row, 'parentId' | 'ord' | 'deleted'> {
  const parentId = f['parentId'];
  const ord = f['ord'];
  const deleted = f['deleted'];
  return {
    parentId: typeof parentId === 'string' ? parentId : null,
    ord: typeof ord === 'string' ? ord : 'a0',
    deleted: deleted === true,
  };
}

/**
 * Apply one patch to a row (null: the row does not exist here yet).
 *
 * A creation patch carries `create` and sets `parentId` and `ord`; `deleted`
 * starts false under the creation HLC unless the patch sets it. A re-delivered
 * creation patch whose `create` matches the row is an ordinary patch — the
 * merge makes the repeat a no-op.
 */
export function applyPatch(row: Row | null, patch: RowPatch, ctx: ApplyContext): ApplyResult {
  const base = baseRowFor(row, patch, ctx);
  if (!base.ok) return { row, applied: [], rejected: base.rejected };
  const refused = contentRejections(base.row, patch, ctx);
  if (refused.length > 0) return { row, applied: [], rejected: refused };

  const set = base.creating && !Object.hasOwn(patch.set, 'deleted') ? { ...patch.set, deleted: false } : patch.set;
  const lww = applyLwwPatch({ f: base.row.f, h: base.row.h }, { set, hlc: patch.hlc });
  const textOutcome = applyTextFields(base.row, lww.row, patch);
  if (!textOutcome.ok) return { row, applied: [], rejected: textOutcome.rejected };

  const applied = [...lww.applied, ...textOutcome.applied].sort();
  if (applied.length === 0) return { row, applied: [], rejected: [] };
  const next: Row = {
    ...base.row,
    ...structuralOf(textOutcome.f),
    f: textOutcome.f,
    h: textOutcome.h,
    text: textOutcome.text,
    version: base.row.version + 1,
    changedAt: ctx.changedAt,
  };
  return { row: next, applied, rejected: [] };
}

export type MergeRowsResult =
  | { readonly ok: true; readonly row: Row; readonly changed: boolean }
  | { readonly ok: false; readonly error: string };

function withoutKeys<T>(record: Readonly<Record<string, T>>, keys: ReadonlySet<string>): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([key]) => !keys.has(key)));
}

type MergedText =
  | { readonly ok: true; readonly changed: boolean; readonly state: string; readonly text: string; readonly hlc: string }
  | { readonly ok: false; readonly rejection: PatchRejection };

type TextSide = { readonly state: string | null; readonly hlc: string | undefined };

/** One text field of a whole-row merge: Yjs for the content, the later of the two clocks for `h`. */
function mergeOneText(path: string, local: TextSide, remote: TextSide): MergedText {
  if (remote.state === null || remote.hlc === undefined) {
    return { ok: false, rejection: rejection('invalid-text-update', path, `text state for ${path} without a clock`) };
  }
  const result = local.state === null ? applyTextUpdate(null, remote.state) : mergeTextStates(local.state, remote.state);
  if (!result.ok) return { ok: false, rejection: rejection('invalid-text-update', path, result.error) };
  const hlc = laterHlc(local.hlc, remote.hlc);
  return { ok: true, changed: result.changed || hlc !== local.hlc, state: result.state, text: result.text, hlc };
}

function mergeTexts(
  local: Row,
  remote: Row,
  lww: { readonly f: Readonly<Record<string, JsonValue>>; readonly h: Readonly<Record<string, string>> },
): TextOutcome {
  const f: Record<string, JsonValue> = { ...lww.f };
  const h: Record<string, string> = { ...lww.h };
  const text: Record<string, string> = { ...local.text };
  const applied: string[] = [];
  for (const [path, remoteState] of Object.entries(remote.text)) {
    const merged = mergeOneText(
      path,
      { state: own(text, path) ?? null, hlc: own(h, path) },
      { state: remoteState, hlc: own(remote.h, path) },
    );
    if (!merged.ok) return { ok: false, rejected: [merged.rejection] };
    if (!merged.changed) continue;
    text[path] = merged.state;
    f[path] = merged.text;
    h[path] = merged.hlc;
    applied.push(path);
  }
  return { ok: true, f, h, text, applied };
}

function sameClocks(a: Readonly<Record<string, string>>, b: Readonly<Record<string, string>>): boolean {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => Object.hasOwn(b, k) && a[k] === b[k]);
}

/**
 * Merge a remote replica of a row into the local one — catch-up
 * (`changedSince`) and the `row.state` echo, where a whole row arrives rather
 * than a patch. LWW per field, Yjs per text field; identity, `source` and
 * `lockedFields` are the local row's (they are fixed at creation).
 *
 * Commutative, associative and idempotent in `f`/`h`/`text` (the version and
 * `changedAt` are the local storage's, not replicated state).
 */
export function mergeRows(local: Row, remote: Row, changedAt: string): MergeRowsResult {
  if (local.rowId !== remote.rowId || local.kind !== remote.kind || local.rundownId !== remote.rundownId) {
    return { ok: false, error: `identity mismatch merging ${remote.rowId} into ${local.rowId}` };
  }
  const textKeys = new Set([...Object.keys(local.text), ...Object.keys(remote.text)]);
  let lww: { f: Readonly<Record<string, JsonValue>>; h: Readonly<Record<string, string>> };
  try {
    lww = mergeLwwRows(
      { f: local.f, h: local.h },
      { f: withoutKeys(remote.f, textKeys), h: withoutKeys(remote.h, textKeys) },
    );
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
  const texts = mergeTexts(local, remote, lww);
  if (!texts.ok) return { ok: false, error: texts.rejected.map((r) => r.message).join('; ') };
  if (sameClocks(local.h, texts.h) && texts.applied.length === 0) return { ok: true, row: local, changed: false };
  const row: Row = {
    ...local,
    ...structuralOf(texts.f),
    f: texts.f,
    h: texts.h,
    text: texts.text,
    version: local.version + 1,
    changedAt,
  };
  return { ok: true, row, changed: true };
}
